import { useState, useEffect, useMemo } from 'react';
import throttle from 'lodash/throttle';

interface PaginationState {
  page: number;
  size: number;
  totalRows: number;
  totalPages: number;
}

interface UsePaginationProps {
  localStorageKey: string;
  initialPage?: number;
  initialSize?: number;
}

export const usePagination = ({ localStorageKey, initialPage = 0, initialSize = 50 }: UsePaginationProps) => {
  const [pagination, setPagination] = useState<PaginationState>(() => {
    try {
      const saved = localStorage.getItem(localStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          typeof parsed.page === 'number' &&
          parsed.page >= 0 &&
          typeof parsed.size === 'number' &&
          parsed.size > 0
        ) {
          return {
            page: parsed.page,
            size: parsed.size,
            totalRows: parsed.totalRows ?? 0,
            totalPages: parsed.totalPages ?? 0,
          };
        }
      }
    } catch (e) {
      console.error(`Failed to parse localStorage for key ${localStorageKey}:`, e);
    }
    return { page: initialPage, size: initialSize, totalRows: 0, totalPages: 0 };
  });

  const saveToLocalStorage = useMemo(
    () =>
      throttle((paginationData: PaginationState) => {
        try {
          localStorage.setItem(localStorageKey, JSON.stringify(paginationData));
        } catch (e) {
          console.error(`Failed to save to localStorage for key ${localStorageKey}:`, e);
        }
      }, 1000),
    [localStorageKey]
  );

  useEffect(() => {
    saveToLocalStorage(pagination);
  }, [pagination, saveToLocalStorage]);

  const onPageChange = (newPage: number) => {
    if (newPage >= 1) {
      setPagination((prev) => ({ ...prev, page: newPage - 1 }));
    }
  };

  const onPageSizeChange = (newPageSize: number) => {
    if (newPageSize > 0) {
      setPagination((prev) => ({ ...prev, size: newPageSize, page: 0 }));
    }
  };

  const updateFromApi = (pageResult?: { page: number; size: number; totalElements: number; totalPages: number }) => {
    if (pageResult && typeof pageResult.totalElements === 'number' && typeof pageResult.totalPages === 'number') {
      setPagination((prev) => {
        if (prev.totalRows !== pageResult.totalElements || prev.totalPages !== pageResult.totalPages) {
          return {
            ...prev,
            totalRows: pageResult.totalElements,
            totalPages: pageResult.totalPages,
          };
        }
        return prev;
      });
    }
  };

  const paginatedQuery = useMemo(
    () => ({ page: pagination.page, size: pagination.size }),
    [pagination.page, pagination.size]
  );

  return {
    pagination,
    paginatedQuery,
    onPageChange,
    onPageSizeChange,
    updateFromApi,
  };
};